
import {defineComponent} from 'vue';
import IconBase from "@/components/IconBase.vue";
import Klapper from "@/components/Klapper.vue";
import BerechnungsdetailsTO from "@/types/BerechnungsdetailsTO";
import BaudarlehenErgebnisTO from "@/types/BaudarlehenErgebnisTO";
import BaudarlehenVergleichsergebnisTO from "@/types/BaudarlehenVergleichsergebnisTO";
import LaufzeitTO from "@/types/LaufzeitTO";
import Tooltip from "@/components/Tooltip.vue";
import SliderTO from "@/types/SliderTO";
import {trackGAEvent} from "@/utils/TrackingUtil";
import numberMixin from "@/mixins/NumberMixin";

export default defineComponent({
    name: 'Berechnungsvergleich',
    components: {Tooltip, Klapper, IconBase},
    mixins: [numberMixin],
    emits: ['initTooltips'],
    data() {
        return {
            selectedSollzinsbindung: 0,
            berechnungsdetails: [] as Array<BerechnungsdetailsTO>,
            detailsToDisplay: [] as Array<BerechnungsdetailsTO>, //Es sollen immer nur 3 (bzw. 2 mobil) der 5 Berechnungsdetails angezeigt werden
            indexOfSollzinsbindungen: new Map<number, number>(), //Map mit <sollzinsbindung, indexImBerechnungsdetailsArray>
            rateToSollzinsbindung: new Map<number, SliderTO>(), //Wird gebraucht um das Tooltip zur Rate zu setzen, (Mapping von Sollzinsbindung zu Rate)
            hasNext: true,
            hasPrevious: true,
            COLS_MOBILE: 2,

            textFussnote: "",
            textHeading: "",
            textSollzinsbindung: "",
            textEffektiverJahreszins: "",
            textHoeheMonatlicheRate: "",
            textGesamtlaufzeit: "",
            textHeadingKlapper: "",
            textRechtlicherHinweis: "",
            textRestschuld: "",
            textZuZahlenderGesamtbetrag: "",
            textNettodarlehen: "",
            textJaehrlicheSollzinsbindung: "",
            textSollzinsbindungGebunden: "",
            textAnzahlRaten: "",
            textTilgungssatz: "",
            textGesamtkosten: "",
            textEintragungskostenGrundbuch: "",
            textFinanzierungsmodell: "",
            textTooltipRestschuld: "",

            /* Da die Komponente inzwischen nur noch ausgefuehrt/geladen wird, wenn ueberhaupt eine Berechnung moeglich ist,
             * funktioniert ggf. die Abfrage nach isInitDataLoaded in diesem Fall nicht, da der Watcher darauf nicht (mehr) greift.
             * Dadurch wurden die Texte nicht aus dem Store geladen.
             * Aus diesem Grund wurde eine zusaetliche Variable textLoadedFromStore eingefuehrt, die dem entgegenwirkt. */
            textLoadedFromStore: false,
        };
    },
    methods: {
        setDetailsToDisplay() {
            let selectedSollzinsbindungIndex = this.indexOfSollzinsbindungen.get(this.selectedSollzinsbindung) as number;
            let detailsLength = this.berechnungsdetails.length;
            let cols = 2;
            let counter = 0;
            let toRemoveHigh = cols - 1;
            let toRemoveLow = 1;
            this.detailsToDisplay = [];

            if (document.body.clientWidth > 600) { //Nicht Mobil
                cols = 3;
            }
            if (selectedSollzinsbindungIndex == detailsLength - 1) { //letzte Sollzinsbindung selektiert
                counter = detailsLength - cols;
                toRemoveHigh = cols - 2;

            } else if (selectedSollzinsbindungIndex != 0) { //nicht die erste Sollzinsbindung selektiert
                counter = selectedSollzinsbindungIndex - (cols % 2);
                toRemoveLow = 0;
            }
            for (let i = 0; i < cols; i++) {
                this.detailsToDisplay[i] = this.berechnungsdetails[i + counter];
            }

            this.hasNext = this.calcHasNext(this.detailsToDisplay[toRemoveHigh].sollzinsbindung.jahre);
            this.hasPrevious = this.calcHasPrevious(this.detailsToDisplay[toRemoveLow].sollzinsbindung.jahre);
        },
        onResize(): void {
            this.setDetailsToDisplay();
        },
        getFormattedLaufzeit(laufzeit: LaufzeitTO): string {
            return laufzeit.jahre + " J./" + laufzeit.monate + " Mon."
        },
        isSelected(sollzinsbindung: number): boolean {
            return this.selectedSollzinsbindung == sollzinsbindung;
        },
        onPreviousClicked(event: Event) {
            event.preventDefault();

            //Tauschen der angezeigten Werte
            for (let i = this.detailsToDisplay.length - 1; i >= 0; i--) {
                let jahre = this.detailsToDisplay[i].sollzinsbindung.jahre;
                this.detailsToDisplay[i] = this.getPreviousElement(jahre)
            }

            let sollzinsbindungToRemove = 0;
            if (this.selectedSollzinsbindung == this.detailsToDisplay[0].sollzinsbindung.jahre) {
                sollzinsbindungToRemove = this.detailsToDisplay[1].sollzinsbindung.jahre;
            } else {
                sollzinsbindungToRemove = this.detailsToDisplay[0].sollzinsbindung.jahre;
            }

            //Ein-/Ausblenden der Buttons
            this.hasNext = this.calcHasNext(sollzinsbindungToRemove);
            this.hasPrevious = this.calcHasPrevious(sollzinsbindungToRemove);

            this.$emit('initTooltips', '.skin-calculator-table-baudarlehen');
            trackGAEvent(this.$store.state.rechnerNameGAEvents, "2_ergebnis_baudarlehen_vergleich_navigation", "back");
        },
        getPreviousElement(jahre: number): BerechnungsdetailsTO {
            let indexOfElement = this.indexOfSollzinsbindungen.get(jahre) as number; //Parsen, weil theoretisch auch undefined aus der Map kommen kann

            if (jahre == this.selectedSollzinsbindung) {
                return this.berechnungsdetails[indexOfElement];
            } else {
                let newIndex = indexOfElement - 1;

                // Wenn z.B. 15, 10, 20 angezeigt werden, soll nicht 10 sondern 5 an Stelle von 15 angezeigt werden
                if (this.selectedSollzinsbindung == this.berechnungsdetails[newIndex].sollzinsbindung.jahre) {
                    newIndex -= 1;
                }
                return this.berechnungsdetails[newIndex];
            }
        },
        onNextClicked(event: Event) {
            event.preventDefault();

            //Tauschen der angezeigten Werte
            let lastIndex = this.detailsToDisplay.length - 1;

            for (let i = 0; i < this.detailsToDisplay.length; i++) {
                this.detailsToDisplay[i] = this.getNextElement(this.detailsToDisplay[i].sollzinsbindung.jahre);
            }

            //Ein-/Ausblenden der Buttons
            let sollzinsbindungToRemove = 0;
            if (this.selectedSollzinsbindung == this.detailsToDisplay[lastIndex].sollzinsbindung.jahre) {
                sollzinsbindungToRemove = this.detailsToDisplay[lastIndex - 1].sollzinsbindung.jahre;
            } else {
                sollzinsbindungToRemove = this.detailsToDisplay[lastIndex].sollzinsbindung.jahre;
            }
            this.hasNext = this.calcHasNext(sollzinsbindungToRemove);
            this.hasPrevious = this.calcHasPrevious(sollzinsbindungToRemove);

            this.$emit('initTooltips', '.skin-calculator-table-baudarlehen');
            trackGAEvent(this.$store.state.rechnerNameGAEvents, "2_ergebnis_baudarlehen_vergleich_navigation", "forward");
        },
        getNextElement(jahre: number): BerechnungsdetailsTO {
            let indexOfElement = this.indexOfSollzinsbindungen.get(jahre);
            indexOfElement = indexOfElement as number; //Parsen, weil theoretisch auch undefined aus der Map kommen kann

            if (jahre == this.selectedSollzinsbindung) {
                return this.berechnungsdetails[indexOfElement];
            } else {
                let newIndex = indexOfElement + 1;

                if (this.selectedSollzinsbindung == this.berechnungsdetails[newIndex].sollzinsbindung.jahre) { // Wenn z.B. 15, 25, 20 angezeigt werden, soll nicht 25 sondern 30 an Stelle von 20 angezeigt werden
                    newIndex += 1;
                }
                return this.berechnungsdetails[newIndex];
            }
        },
        /* Ermittelt, ob ein vorheriger/kleinerer Sollzinsbindungswert angezeigt werden kann. */
        calcHasPrevious(sollzinsbindung: number): boolean {
            let previousSollzinsbindungIndex = this.indexOfSollzinsbindungen.get(sollzinsbindung) as number; // Vorheriger zu betrachtender Sollzinsbindungs-Index
            let selectedSollzinsbindungIndex = this.indexOfSollzinsbindungen.get(this.selectedSollzinsbindung); // Index der gewaehlten Sollzinsbindung

            //Wenn das erste Element selektiert wurde, soll das 2. Element aus der Anzeige entfernt werden
            if (0 === selectedSollzinsbindungIndex) {
                return previousSollzinsbindungIndex > 1;
            } else {
                return previousSollzinsbindungIndex > 0;
            }
        },
        /* Ermittelt, ob ein nächst größerer Sollzinsbindungswert angezeigt werden kann. */
        calcHasNext(sollzinsbindung: number): boolean {
            let nextSollzinsbindungIndex = this.indexOfSollzinsbindungen.get(sollzinsbindung) as number; // Naechster zu betrachtender Sollzinsbindungs-Index
            let selectedSollzinsbindungIndex = this.indexOfSollzinsbindungen.get(this.selectedSollzinsbindung); // Index der gewaehlten Sollzinsbindung

            //Wenn das letzte Element selektiert wurde, soll das 2. letzte Element entfernt werden z. B. 10, 15, 30 -> 5, 10, 30
            if (this.berechnungsdetails.length - 1 === selectedSollzinsbindungIndex) {
                return nextSollzinsbindungIndex < this.berechnungsdetails.length - 2;
            } else {
                // Sonderfall - Vorletzter Wert vorausgewählt (Beispiel)
                // Beim Laden der APP ist nextSollzinsbindungIndex == 3 (20 Jahre) und selectedSollzinsbindungIndex == 3 (20 Jahre)
                // beim normalen rotieren wäre nextSollzinsbindungIndex == 4 (25 Jahre), dann wuerde kein Button mehr angezeigt werden,
                // da kein Index 5 (30 Jahre) existiert
                if (selectedSollzinsbindungIndex === nextSollzinsbindungIndex && this.berechnungsdetails.length - 2 === nextSollzinsbindungIndex) { // beim Laden der APP kann das vorkommen
                    return nextSollzinsbindungIndex < this.berechnungsdetails.length - 2;
                }
                return nextSollzinsbindungIndex < this.berechnungsdetails.length - 1;
            }
        },
        getHinweisEndfaelligkeit(berechnungsdetails: BerechnungsdetailsTO): string {
            let hinweis = "";

            if (null !== berechnungsdetails.endfaelligkeit && undefined !== berechnungsdetails.endfaelligkeit) {
                let jahre = berechnungsdetails.gesamtlaufzeit.jahre;
                let betrag = berechnungsdetails.endfaelligkeit.betrag;
                let datum = berechnungsdetails.endfaelligkeit.datum;

                let text = this.textTooltipRestschuld;
                text = text.replace("@jahre", jahre.toString());
                text = text.replace("@betrag", this.convertNumberWithDigits(betrag));
                text = text.replace("@datum", datum);
                hinweis = text;
            }
            return hinweis;
        },
        getTooltipRate(detail: BerechnungsdetailsTO): string {
            let text = "";
            let sollzinsbindung = detail.sollzinsbindung.jahre;
            let sliderRate = this.rateToSollzinsbindung.get(sollzinsbindung) as SliderTO;
            if (sliderRate !== undefined && sliderRate.hinweis !== undefined && sliderRate.hinweis !== null) {
                text = sliderRate.hinweis.text;
            }
            return text;
        },
        getTextFromStore() {
            this.textFussnote = this.$store.state.fachtexte["rechner.berechnungsdetails.querverweise"];
            this.textHeading = this.$store.state.fachtexte["rechner.berechnungsdetails_vergleich_header"];
            this.textSollzinsbindung = this.$store.state.fachtexte["rechner.berechnungsdetails.sollzinsbindung"];
            this.textEffektiverJahreszins = this.$store.state.fachtexte["rechner.berechnungsdetails.effektiver.jahreszins"];
            this.textHoeheMonatlicheRate = this.$store.state.fachtexte["rechner.berechnungsdetails.monatliche.rate"];
            this.textGesamtlaufzeit = this.$store.state.fachtexte["rechner.berechnungsdetails.gesamtlaufzeit"];
            this.textHeadingKlapper = this.$store.state.fachtexte["rechner.berechnungsdetails.vergleich_klapper"];
            this.textRechtlicherHinweis = this.$store.state.fachtexte["rechner.berechnungsdetails.rechtlicher.text"];
            this.textRestschuld = this.$store.state.fachtexte["rechner.berechnungsdetails.restschuld"];
            this.textZuZahlenderGesamtbetrag = this.$store.state.fachtexte["rechner.berechnungsdetails.gesamtbeitrag"];
            this.textNettodarlehen = this.$store.state.fachtexte["rechner.berechnungsdetails.nettodarlehen"];
            this.textJaehrlicheSollzinsbindung = this.$store.state.fachtexte["rechner.berechnungsdetails.jaehrl_sollzinsbindung"];
            this.textSollzinsbindungGebunden = this.$store.state.fachtexte["rechner.berechnungsdetails.sollzinsbindung"];
            this.textAnzahlRaten = this.$store.state.fachtexte["rechner.berechnungsdetails.anzahl.raten"];
            this.textTilgungssatz = this.$store.state.fachtexte["rechner.berechnungsdetails.tilgungssatz"];
            this.textGesamtkosten = this.$store.state.fachtexte["rechner.berechnungsdetails.gesamtkosten"];
            this.textEintragungskostenGrundbuch = this.$store.state.fachtexte["rechner.berechnungsdetails.eintragungskosten.grundbuch"];
            this.textFinanzierungsmodell = this.$store.state.fachtexte["rechner.berechnungsdetails.finanzierungsmodell"];
            this.textTooltipRestschuld = this.$store.state.fachtexte["rechner.berechnungsdetails.hinweis.endfaelligkeit"];

            this.textLoadedFromStore = true;
        }
    },
    computed: {
        isCalculating(): boolean {
            return this.$store.state.isCalculatingVergleich;
        },
        vergleichsmodelle(): BaudarlehenVergleichsergebnisTO {
            return this.$store.state.vergleichsmodelle;
        },
        isInitDataLoaded(): boolean {
            return this.$store.state.initDataLoaded;
        },
    },
    watch: {
        vergleichsmodelle() {
            let vergleichsergebnisse: Array<BaudarlehenErgebnisTO> = this.vergleichsmodelle.vergleichsergebnisse;
            this.indexOfSollzinsbindungen.clear();
            for (let i = 0; i < vergleichsergebnisse.length; i++) {
                this.berechnungsdetails[i] = vergleichsergebnisse[i].berechnungsdetails

                let sollzinsbindung = this.berechnungsdetails[i].sollzinsbindung.jahre;
                this.indexOfSollzinsbindungen.set(sollzinsbindung, i);

                this.rateToSollzinsbindung.set(sollzinsbindung, vergleichsergebnisse[i].rate);
            }
            this.selectedSollzinsbindung = this.vergleichsmodelle.selectedSollzinsbindung;
            this.setDetailsToDisplay();
            this.$emit('initTooltips', '.skin-calculator-table-baudarlehen');

            if (!this.textLoadedFromStore) {
                this.getTextFromStore();
            }
        },
        isInitDataLoaded(isLoaded: boolean): void {
            if (isLoaded) {
                this.getTextFromStore();
                this.textLoadedFromStore = true;
            }
        }
    },
    mounted() {
        window.addEventListener('resize', this.onResize);
    },
    unmounted() {
        window.removeEventListener('resize', this.onResize);
    }
});
